import {
  valueDataToString,
  valueDataToStringsWithDelimenters,
  valueDataVToString,
} from "@smartsuite/automation-conditions";
import type {
  ActionOutputData,
  InputData,
  OutputData,
  RecordJobData,
} from "@smartsuite/automation-engine-api";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import {
  translateAction,
  translateIfPresent,
} from "../../../../../locales/i18n";
import { useRootStore } from "../../../../../stores/hooks";
import type { ActionCandidate } from "../../../../../types/candidate";
import {
  OUTPUT_APPLICATION_ID,
  RECORD_ID,
} from "../../../../../utils/constants";
import { capitalizeFirstLetter } from "../../../../../utils/strings";
import {
  addEmptyValueIfNeeded,
  getErrorMessage,
  getInputLabel,
} from "../../../ActivityGrid/ActivityGrid.helpers";
import { ActionError } from "../ActionError/ActionError";
import { ActivityDetailsGroup } from "../ActivityDetailsGroup/ActivityDetailsGroup";
import { ActivityDetailsItem } from "../ActivityDetailsItem/ActivityDetailsItem";
import { ActivityDetailsTriggerGroup } from "../ActivityDetailsTriggerGroup/ActivityDetailsTriggerGroup";

interface GeneralActionResultGroupProps {
  action: ActionOutputData;
  onDetailsClick?: () => void;
  recordJobData?: RecordJobData;
}

export const AIActionResultGroup = observer<GeneralActionResultGroupProps>(
  ({ action, onDetailsClick, recordJobData }) => {
    const { integrations, automations, jobDetails } = useRootStore();

    const [showFullPrompt, setShowFullPrompt] = useState<boolean>(false);
    const actions = automations.candidate.allActions;
    const selectedIntegration = integrations.list.find(
      (i) => i.integrationId === action.actionReference?.integrationId,
    );
    const selectedAction = selectedIntegration?.actions.find(
      (a) => a.actionId === action.actionReference?.actionId,
    );
    const [selectedActionFallback, setSelectedActionFallback] = useState<
      ActionCandidate | undefined
    >();

    useEffect(() => {
      setSelectedActionFallback(
        automations.candidate.self.actions.find(
          (actionInput: ActionCandidate) =>
            actionInput.actionReference?.instanceId ===
            action.actionReference?.instanceId,
        ),
      );
    }, [automations.candidate]);

    const getInputValue = (
      input: InputData,
      recordJob?: RecordJobData,
    ): string => {
      if (input.value === undefined) {
        return "";
      }
      if (input.inputId === "propmt") {
        let value = "";
        if (
          input.value.values.length > 0 &&
          input.value.values[0].value.oneofKind === "valueDataJson"
        ) {
          value = JSON.stringify(
            JSON.parse(input.value.values[0].value.valueDataJson),
            null,
            4,
          );
          value = value.replace(/\n+/g, "<br>");
          value = value.replace(/\s\s+/g, "&nbsp;&nbsp;");
        }
        return value;
      }
      if (recordJob !== undefined) {
        if (recordJob.outputData?.recordList?.recordList !== undefined) {
          const output =
            recordJob.outputData.recordList.recordList.records[0]?.outputs.find(
              (output) => output.outputId === input.inputId,
            );

          if (output?.value !== undefined) {
            return valueDataToStringsWithDelimenters(
              output.value,
              jobDetails.job.automation?.timezone,
            ).join("; ");
          }
        }
      }
      return valueDataToStringsWithDelimenters(
        input.value,
        jobDetails.job.automation?.timezone,
      ).join("; ");
    };

    const status = action.status.oneofKind === "ok" ? "is_success" : "is_error";

    const errorMessage = getErrorMessage(action, jobDetails.job);

    const getLabel = (input: InputData, index: number): string => {
      if (input.inputId === "output_type") {
        const label = "Output Type";
        return translateIfPresent(`caption.${label}`, label);
      }
      const label = getInputLabel(
        input,
        jobDetails,
        action,
        actions,
        selectedActionFallback,
        index,
      );
      // The dynamic model input is saved without a label in the action config
      if (label === "" && input.inputId === "model") {
        const modelLabel = "Model";
        return translateIfPresent(`caption.${modelLabel}`, modelLabel);
      }
      return label;
    };

    const getValue = (input: InputData, recordJob: RecordJobData): string => {
      // replace prompt from input to prompt from outputs
      if (input.inputId === "propmt") {
        const llmPrompt = recordJobData?.outputData?.outputs.find(
          (output) => output.outputId === "llm-prompt",
        );
        if (
          llmPrompt?.value !== undefined &&
          llmPrompt.value.values.length > 0 &&
          llmPrompt.value.values[0].value.oneofKind === "string"
        ) {
          let value = llmPrompt.value.values[0].value.string;
          value = value.replace(/\\n+/g, "<br>");
          value = value.replace(/\s\s+/g, "&nbsp;&nbsp;");
          return value;
        }
      }
      if (
        input.inputId === "suppress_error" ||
        input.inputId === "output_type"
      ) {
        const label = capitalizeFirstLetter(
          input.value?.values[0].value.oneofKind === "string"
            ? input.value?.values[0].value.string
            : "",
        );
        return translateIfPresent(`caption.${label}`, label);
      }
      return addEmptyValueIfNeeded(getInputValue(input, recordJob));
    };

    const output = recordJobData?.outputData?.outputs.find(
      (output) => output.outputId === "ai-response",
    );

    const getOutputValue = (output: OutputData): string => {
      if (output?.value !== undefined && output.value?.values.length > 0) {
        if (output.value.values[0].value.oneofKind === "list") {
          return output.value.values[0].value.list.values
            .map((v) => valueDataToString(v))
            .join("<br/>");
        }
        return valueDataVToString(output.value.values[0]);
      }
      return "";
    };

    const restOutputs =
      (
        recordJobData?.outputData?.outputs.filter(
          (output) => output.outputId !== "ai-response",
        ) ?? []
      ).sort((a, b) => {
        if (a.outputId === "llm-prompt") return -1;
        return 0;
      }) ?? [];

    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.metaKey && e.which === 13) {
        setShowFullPrompt(!showFullPrompt);
      }
    };

    // On Command+Enter - we want to show Full Prompt on run history
    useEffect(() => {
      document.addEventListener("keydown", onKeyDown);
      return () => {
        document.removeEventListener("keydown", onKeyDown);
        setShowFullPrompt(false);
      };
    }, []);

    return (
      <ActivityDetailsGroup
        label={
          selectedAction !== undefined ? translateAction(selectedAction) : ""
        }
        status={status}
        onClick={onDetailsClick}
      >
        {status === "is_error" && <ActionError errorMessage={errorMessage} />}
        <ActivityDetailsTriggerGroup title={`Record 0`} onClick={() => {}}>
          {recordJobData?.inputs
            .filter(
              (input) =>
                ![
                  RECORD_ID,
                  OUTPUT_APPLICATION_ID,
                  "output_content",
                  "propmt",
                ].includes(input.inputId),
            )
            .flatMap((input) => {
              return (
                <ActivityDetailsItem
                  key={input.inputId}
                  label={getLabel(input, 0)}
                >
                  <span
                    dangerouslySetInnerHTML={{
                      __html: getValue(input, recordJobData),
                    }}
                  />
                </ActivityDetailsItem>
              );
            })}
          {restOutputs.map((output) => {
            if (!showFullPrompt && output.outputId === "full-llm-prompt") {
              return null;
            }
            return (
              <ActivityDetailsItem
                key={output.outputId}
                label={output.outputLabel}
              >
                <span
                  dangerouslySetInnerHTML={{
                    __html: addEmptyValueIfNeeded(getOutputValue(output)),
                  }}
                />
              </ActivityDetailsItem>
            );
          })}
          {output !== undefined && (
            <ActivityDetailsItem label={output.outputLabel}>
              {addEmptyValueIfNeeded(getOutputValue(output))}
            </ActivityDetailsItem>
          )}
        </ActivityDetailsTriggerGroup>
      </ActivityDetailsGroup>
    );
  },
);
